"use client";

import { Translate } from "@/components/Translate";
import { GeoSelectorTrigger } from "@/features/navigation/GeoSelector";
import { useTranslations } from "@/i18n/client";
import { countries, useGeo } from "@/i18n/routing";
import { useCentraClient } from "@frend-digital/centra/client";
import type { GeoLocale } from "@frend-digital/next/i18n";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import React, { useState } from "react";
import {
	AlertDialogActions,
	AlertDialogClose,
	AlertDialogContent,
	AlertDialogRoot,
	AlertDialogTitle,
	AlertDialogViewport,
} from "../AlertDialog";
import { Button } from "../Button";
import { useDraftMode } from "../DraftmodeContext";
import styles from "./GeoPopup.module.css";

const storage = createJSONStorage(() => window.sessionStorage);
const openAtom = atomWithStorage("geo-popup-open", false, storage);

export type GeoPopupProps = {};

export const GeoPopup = ({}: GeoPopupProps) => {
	const geo = useGeoPopup();
	const t = useTranslations();
	const [changeLanguage, setChangeLanguage] = useState(false);
	const isEnabled = useDraftMode();

	const browserCountry =
		t(`markets.${geo.browserCountryInfo?.country}`) || geo.browserCountryInfo?.name;
	const currentCountry =
		t(`markets.${geo.currentCountryInfo?.country}`) || geo.currentCountryInfo?.name;

	if (isEnabled) return null;

	return (
		<AlertDialogRoot
			open={geo.shouldOpen}
			onOpenChange={(open) => {
				if (!open) {
					geo.close(changeLanguage);
				}
			}}
		>
			<AlertDialogViewport lock={false}>
				<AlertDialogContent>
					{geo.isUnsupportedMarket ? (
						<>
							<AlertDialogTitle>
								<Translate
									params={{ country: geo.browserCountryName ?? "" }}
									children="No local store for {{country}}"
								/>
							</AlertDialogTitle>
							<p>
								<Translate children="Choose a market to continue shopping, or keep browsing the current store." />
							</p>
							<AlertDialogActions>
								<GeoSelectorTrigger asChild>
									<Button
										className={styles.button}
										size="caption"
										color="black"
										onClick={() => geo.close(false)}
									>
										<Translate children="Choose market" />
									</Button>
								</GeoSelectorTrigger>
								<AlertDialogClose asChild>
									<Button
										className={styles.button}
										size="caption"
										onClick={() => geo.close(false)}
										color="outlined"
									>
										<Translate params={{ currentCountry: currentCountry }}>
											{/* biome-ignore lint/style/noUnusedTemplateLiteral: <explanation> */}
											{`Keep browsing {{currentCountry}}`}
										</Translate>
									</Button>
								</AlertDialogClose>
							</AlertDialogActions>
						</>
					) : (
						<>
							<AlertDialogTitle>
								<Translate
									params={{
										country: browserCountry,
									}}
									children="Shopping from {{country}}?"
								/>
							</AlertDialogTitle>
							<p>
								<Translate
									params={{
										country: browserCountry,
									}}
									children="Get faster delivery, and exclusive deals on our {{country}} store."
								/>
							</p>

							<div>
								<label htmlFor="language-change" className={styles.label}>
									<input
										id="language-change"
										type="checkbox"
										className={styles.checkboxInput}
										checked={changeLanguage}
										onChange={(e) => setChangeLanguage(e.target.checked)}
									/>
									<Translate
										params={{
											lang: t(`language.${geo.browserCountryInfo?.language}`).toLowerCase(),
										}}
									>
										{/* biome-ignore lint/style/noUnusedTemplateLiteral: <explanation> */}
										{`Change language to {{lang}}`}
									</Translate>
								</label>
							</div>
							<AlertDialogActions>
								<Button
									className={styles.button}
									size="caption"
									loading={geo.pending}
									onClick={() => geo.changeCountry(changeLanguage)}
									color="black"
								>
									<Translate
										params={{
											country: browserCountry,
										}}
									>
										{/* biome-ignore lint/style/noUnusedTemplateLiteral: <explanation> */}
										{`Shop {{country}} store`}
									</Translate>
								</Button>
								<AlertDialogClose asChild>
									<Button
										className={styles.button}
										size="caption"
										onClick={() => geo.close(changeLanguage)}
										color="outlined"
									>
										<Translate
											params={{
												currentCountry: currentCountry,
											}}
										>
											{/* biome-ignore lint/style/noUnusedTemplateLiteral: <explanation> */}
											{`Keep browsing {{currentCountry}}`}
										</Translate>
									</Button>
								</AlertDialogClose>
							</AlertDialogActions>
						</>
					)}
				</AlertDialogContent>
			</AlertDialogViewport>
		</AlertDialogRoot>
	);
};

const useGeoPopup = () => {
	const { country, action, language, pending } = useGeo();
	const client = useCentraClient();

	const [requestedClose, setRequestedClose] = useAtom(openAtom);

	const { data: browserLocation, error } = useQuery({
		queryKey: ["geo-matcher"],
		queryFn: async () => {
			const { data, error } = await client.GET("/selection", {
				headers: {
					"API-Token": "",
				},
			});

			if (error) {
				throw error;
			}

			return data.location ?? null;
		},
	});

	const browserCountry = browserLocation?.country;
	const browserCountryName = browserLocation?.name;

	const isLoading = !browserLocation && !error;

	const isMismatch = country !== browserCountry;

	const browserCountryInfo = countries.find((c) => c.country === browserCountry);
	const currentCountryInfo = countries.find((c) => c.country === country);

	const shouldOpen = isMismatch && !isLoading && !requestedClose && !error;
	const isUnsupportedMarket = !browserCountryInfo;

	const close = (shouldChangeLanguage = false) => {
		setRequestedClose(true);

		if (shouldChangeLanguage && browserCountryInfo?.language) {
			const targetLanguage = browserCountryInfo.language.toLowerCase();
			action(`${targetLanguage}-${country.toUpperCase()}` as GeoLocale);
		}
	};

	const changeCountry = (shouldChangeLanguage: boolean) => {
		setRequestedClose(true);
		const targetLanguage =
			shouldChangeLanguage && browserCountryInfo?.language
				? browserCountryInfo.language.toLowerCase()
				: language.toLowerCase();

		action(`${targetLanguage}-${browserCountry?.toUpperCase()}` as GeoLocale);
	};

	return {
		isMismatch,
		isLoading,
		shouldOpen,
		isUnsupportedMarket,
		browserCountryName,
		close,
		browserCountryInfo,
		currentCountryInfo,
		changeCountry,
		pending,
	};
};
